import React from 'react';
import { BookOpen, Building2, BriefcaseBusiness, Image, LucideIcon, UserRound, Waypoints } from 'lucide-react';
import { ENTITY_TONE, EntityKind, hexAlpha, isHexColor, readableOn } from './colors';

const ENTITY_ICON: Record<EntityKind, LucideIcon> = {
  stakeholder: UserRound,
  organization: Building2,
  wiki: BookOpen,
  media: Image,
  assignment: BriefcaseBusiness,
  relationship: Waypoints,
  user: UserRound,
};

const TILE_SIZES = {
  xs: { box: 'h-5 w-5 rounded-md', icon: 'h-3 w-3' },
  sm: { box: 'h-7 w-7 rounded-lg', icon: 'h-3.5 w-3.5' },
  md: { box: 'h-8 w-8 rounded-[10px]', icon: 'h-4 w-4' },
};

/** Soft tinted tile with the icon of an entity kind, for mixed lists (search, activity, pending) */
export const EntityIcon: React.FC<{
  kind: EntityKind;
  size?: keyof typeof TILE_SIZES;
  icon?: LucideIcon;
  className?: string;
  title?: string;
}> = ({ kind, size = 'sm', icon, className = '', title }) => {
  const Icon = icon || ENTITY_ICON[kind];
  const s = TILE_SIZES[size];
  return (
    <span
      title={title}
      aria-hidden={title ? undefined : true}
      className={`inline-flex flex-shrink-0 items-center justify-center ${s.box} ${ENTITY_TONE[kind].soft} ${className}`}
    >
      <Icon className={s.icon} strokeWidth={1.75} />
    </span>
  );
};

const AVATAR_SIZES = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-7 w-7 text-[11px]',
  md: 'h-9 w-9 text-[12px]',
  lg: 'h-14 w-14 text-[18px]',
  xl: 'h-20 w-20 text-[26px]',
};

export function initialsOf(name: string | undefined | null, max = 2): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const letters = parts.length === 1 ? [parts[0][0]] : [parts[0][0], parts[parts.length - 1][0]];
  return letters.slice(0, max).join('').toUpperCase();
}

/**
 * Initials avatar tinted by entity kind: people sky, organizations teal (square-ish so they read
 * as organizations). Pass `image` (e.g. an <AuthImage/>) to show a photo or logo instead.
 */
export const Avatar: React.FC<{
  name?: string | null;
  initials?: string;
  kind?: 'stakeholder' | 'organization' | 'neutral';
  size?: keyof typeof AVATAR_SIZES;
  image?: React.ReactNode;
  className?: string;
}> = ({ name, initials, kind = 'stakeholder', size = 'sm', image, className = '' }) => {
  const shape = kind === 'organization' ? (size === 'lg' || size === 'xl' ? 'rounded-2xl' : 'rounded-lg') : 'rounded-full';
  const tone = kind === 'neutral' ? 'bg-neutral-100 text-neutral-600' : ENTITY_TONE[kind].soft;
  return (
    <span
      aria-hidden="true"
      className={`inline-flex flex-shrink-0 items-center justify-center overflow-hidden font-medium ${shape} ${AVATAR_SIZES[size]} ${
        image ? 'bg-neutral-100' : tone
      } ${className}`}
    >
      {image || initials || initialsOf(name)}
    </span>
  );
};

/** Dot in an arbitrary colour, e.g. a taxonomy colour */
export const Swatch: React.FC<{ color?: string | null; size?: 'sm' | 'md'; className?: string; title?: string }> = ({
  color,
  size = 'sm',
  className = '',
  title,
}) => (
  <span
    title={title}
    aria-hidden={title ? undefined : true}
    className={`inline-block flex-shrink-0 rounded-full ${size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5'} ${
      isHexColor(color) ? '' : 'bg-neutral-300'
    } ${className}`}
    style={isHexColor(color) ? { backgroundColor: color } : undefined}
  />
);

/**
 * Category/type label. With a taxonomy colour it becomes a soft tinted pill with a dot;
 * without one it stays a quiet outline pill.
 */
export const CategoryPill: React.FC<{ label: React.ReactNode; color?: string | null; className?: string; title?: string }> = ({
  label,
  color,
  className = '',
  title,
}) =>
  isHexColor(color) ? (
    <span
      title={title}
      className={`inline-flex h-5 max-w-full items-center gap-1.5 whitespace-nowrap rounded-full px-2 text-[11px] font-medium leading-none ${className}`}
      style={{ backgroundColor: hexAlpha(color, 0.1), color: readableOn(color) }}
    >
      <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full" style={{ backgroundColor: color }} />
      <span className="truncate">{label}</span>
    </span>
  ) : (
    <span
      title={title}
      className={`inline-flex h-5 max-w-full items-center whitespace-nowrap rounded-full px-2 text-[11px] font-medium leading-none text-neutral-500 ring-1 ring-inset ring-neutral-200 ${className}`}
    >
      <span className="truncate">{label}</span>
    </span>
  );

/** Thin horizontal score bar in a taxonomy colour (0–100) */
export const ScoreBar: React.FC<{ value: number; color?: string | null; className?: string }> = ({
  value,
  color,
  className = '',
}) => {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <span className={`block h-1.5 w-full overflow-hidden rounded-full bg-neutral-100 ${className}`} aria-hidden="true">
      <span
        className="block h-full rounded-full"
        style={{ width: `${pct}%`, backgroundColor: isHexColor(color) ? color : '#0a0a0a' }}
      />
    </span>
  );
};
